import { Box, Button, StaticDateRangePicker } from "@outschool/backpack";
import { Search } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { FilterList } from "@outschool/ui-components-website";
import { useImpressionTracking } from "@outschool/ui-legacy-component-library";
import React from "react";

import { FILTER_FORMAT } from "./common";
import FilterActionButtons from "./desktop/FilterActionButtons";

type DateFiltersProps = {
  handleChangesExternally?: boolean;
  isMobile?: boolean;
  onCancel: () => void;
  onChange: (filters: SearchFiltersType) => void;
  onShortcutSelected: (filters: SearchFiltersType) => void;
  value: SearchFiltersType;
};

const getStartAndEndDateFromRelativeDateFilter = (
  relativeDateFilter: string | null | undefined
) => {
  const today = dayjs();
  switch (relativeDateFilter) {
    case Search.RelativeDateFilter.AllUpcoming:
      return [null, null];
    case Search.RelativeDateFilter.Today:
      return [today.startOf("day"), today.endOf("day")];
    case Search.RelativeDateFilter.SevenDays:
      return [today, today.add(7, "day").endOf("day")];
    case Search.RelativeDateFilter.FourteenDays:
      return [today, today.add(14, "day").endOf("day")];
    case Search.RelativeDateFilter.Summer:
      const startOfSummer = today.startOf("month").month(5);
      const endOfSummer = today.endOf("month").month(7);

      if (today.isAfter(endOfSummer)) {
        startOfSummer.add(1, "year");
        endOfSummer.add(1, "year");
      } else if (today.isAfter(startOfSummer) && today.isBefore(endOfSummer)) {
        return [today, endOfSummer];
      }
      return [today.startOf("month").month(5), today.endOf("month").month(7)];
    case Search.RelativeDateFilter.Fall:
      const startOfFall = today.startOf("month").month(8);
      const endOfFall = today.endOf("month").month(10);

      if (today.isAfter(endOfFall)) {
        startOfFall.add(1, "year");
        endOfFall.add(1, "year");
      } else if (today.isAfter(startOfFall) && today.isBefore(endOfFall)) {
        return [today, endOfFall];
      }
      return [today.startOf("month").month(8), today.endOf("month").month(10)];
    default:
      return [null, null];
  }
};

export default function DateFilters({
  handleChangesExternally = false,
  isMobile = false,
  onCancel,
  onChange,
  onShortcutSelected,
  value
}: DateFiltersProps) {
  const filters = value;
  const [impressionTrackingNode, setImpressionTrackingNode] =
    React.useState(null);
  const { trackTouch } = useImpressionTracking({
    node: impressionTrackingNode,
    trackingLabel: "date_filter",
    uniqueId: "date_filter"
  });

  const { t } = useTranslation("client\\components\\search\\DateFilters");

  const [{ startDate, endDate }, setStartAndEndDate] = React.useState<{
    startDate: dayjs.Dayjs | null;
    endDate: dayjs.Dayjs | null;
  }>({ startDate: null, endDate: null });

  React.useEffect(() => {
    if (
      [
        Search.RelativeDateFilter.Today,
        Search.RelativeDateFilter.SevenDays,
        Search.RelativeDateFilter.FourteenDays,
        Search.RelativeDateFilter.Summer,
        Search.RelativeDateFilter.Fall,
        Search.RelativeDateFilter.AllUpcoming
      ].some(val => val === filters.startAfter)
    ) {
      const startEndDates = getStartAndEndDateFromRelativeDateFilter(
        filters.startAfter
      );
      setStartAndEndDate({
        startDate: startEndDates[0],
        endDate: startEndDates[1]
      });
    } else {
      setStartAndEndDate({
        startDate:
          !filters.startAfter && filters.startBefore
            ? dayjs().startOf("day")
            : filters.startAfter
            ? dayjs(filters.startAfter)
            : null,
        endDate: filters.startBefore ? dayjs(filters.startBefore) : null
      });
    }
  }, [filters.startAfter, filters.startBefore]);

  const shortcutsItems = React.useMemo(
    () =>
      [
        {
          id: Search.RelativeDateFilter.AllUpcoming,
          label: t("All upcoming dates")
        },
        { id: Search.RelativeDateFilter.Today, label: t("Today") },
        { id: Search.RelativeDateFilter.SevenDays, label: t("Next 7 days") },
        {
          id: Search.RelativeDateFilter.FourteenDays,
          label: t("Next 14 days")
        },
        { id: Search.RelativeDateFilter.Summer, label: t("Summer") },
        { id: Search.RelativeDateFilter.Fall, label: t("Fall") }
      ].map(shortcut => ({
        ...shortcut,
        getValue: () => getStartAndEndDateFromRelativeDateFilter(shortcut.id)
      })),
    [t]
  );

  const ShortcutsComponent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}
    >
      {shortcutsItems.map(shortcut => (
        <Button
          color="neutral"
          key={shortcut.label}
          sx={{
            marginY: 5
          }}
          variant={
            dayjs(startDate?.format(FILTER_FORMAT)).isSame(
              dayjs(shortcut.getValue()[0]).format(FILTER_FORMAT)
            ) &&
            dayjs(endDate?.format(FILTER_FORMAT)).isSame(
              dayjs(shortcut.getValue()[1]).format(FILTER_FORMAT)
            )
              ? "contained"
              : "outlined"
          }
          onClick={() => {
            const [start, end] = shortcut.getValue();
            if (handleChangesExternally) {
              onShortcutSelected({
                ...filters,
                startAfter: start?.format(FILTER_FORMAT),
                startBefore: end?.format(FILTER_FORMAT)
              });
            } else {
              setStartAndEndDate({
                startDate: start,
                endDate: end
              });
            }
          }}
        >
          {shortcut.label}
        </Button>
      ))}
    </Box>
  );

  return (
    <FilterList
      ref={setImpressionTrackingNode}
      sx={{
        gridGap: null,
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row"
        }}
      >
        <ShortcutsComponent />
        <StaticDateRangePicker
          calendars={isMobile ? 1 : 2}
          disableAutoMonthSwitching={true}
          disablePast={true}
          localeText={{
            toolbarTitle: ""
          }}
          slotProps={{
            actionBar: {
              sx: {
                display: "none"
              }
            },
            toolbar: {
              sx: {
                display: "none"
              }
            }
          }}
          value={[startDate, endDate]}
          onRangePositionChange={position => {
            if (position === "start" && handleChangesExternally) {
              onCancel?.(); // Teardown / Closes dropdown
            }
          }}
          onChange={([start, end]) => {
            trackTouch();
            if (handleChangesExternally) {
              onChange({
                ...filters,
                startAfter: start?.format(FILTER_FORMAT),
                startBefore: end?.format(FILTER_FORMAT)
              });
            } else {
              setStartAndEndDate({
                startDate: start,
                endDate: end
              });
            }
          }}
        />
      </Box>
      {handleChangesExternally ? null : (
        <FilterActionButtons
          onApply={() => {
            onChange({
              ...filters,
              startAfter: startDate?.format(FILTER_FORMAT),
              startBefore: endDate?.format(FILTER_FORMAT)
            });
          }}
          onCancel={onCancel}
        />
      )}
    </FilterList>
  );
}
